import * as THREE from "./vendor/three.module.min.js";
import { RGBELoader } from "./vendor/RGBELoader.js";

const clamp = (value, min = 0, max = 1) => Math.max(min, Math.min(max, value));
const smooth = (value) => {
  const v = clamp(value);
  return v * v * (3 - 2 * v);
};
const mix = (a, b, amount) => a + (b - a) * amount;
const fadeWindow = (value, start, inEnd, outStart, end) =>
  smooth((value - start) / Math.max(.0001, inEnd - start)) *
  (1 - smooth((value - outStart) / Math.max(.0001, end - outStart)));

export function mountFusionWorld(container) {
  const mobile = matchMedia("(max-width: 760px)").matches;
  const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;
  const renderer = new THREE.WebGLRenderer({
    antialias: false,
    alpha: false,
    powerPreference: "high-performance",
  });
  const areaLimit = Math.sqrt(1180000 / Math.max(1, innerWidth * innerHeight));
  let pixelRatio = Math.min(devicePixelRatio || 1, mobile ? 1.08 : 1.5, Math.max(.72, areaLimit));
  renderer.setPixelRatio(pixelRatio);
  renderer.setClearColor(0x020202, 1);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.08;
  const canvas = renderer.domElement;
  canvas.setAttribute("aria-hidden", "true");
  container.appendChild(canvas);

  const scene = new THREE.Scene();
  const darkBackground = new THREE.Color(0x020202);
  const paleBackground = new THREE.Color(0xe9e9e4);
  const paleSignal = new THREE.Color(0xf4f4ef);
  const darkSignal = new THREE.Color(0x111111);
  const background = new THREE.Color();
  const signal = new THREE.Color();
  scene.background = darkBackground.clone();
  scene.fog = new THREE.FogExp2(0x020202, mobile ? .0102 : .0082);

  const camera = new THREE.PerspectiveCamera(mobile ? 64 : 54, 1, .1, 700);
  scene.add(camera);

  const ambient = new THREE.AmbientLight(0xffffff, .22);
  const key = new THREE.DirectionalLight(0xffffff, 3.1);
  key.position.set(-11, 18, 16);
  const rim = new THREE.DirectionalLight(0xffffff, 2.2);
  rim.position.set(18, -8, -24);
  const signalLight = new THREE.PointLight(0xffffff, 12, 105, 2);
  signalLight.position.set(0, 3, -19);
  scene.add(ambient, key, rim, signalLight);

  const lineMaterials = [];
  const lineMaterial = (opacity, color = 0xf4f4ef) => {
    const material = new THREE.LineBasicMaterial({
      color,
      transparent: true,
      opacity,
      depthWrite: false,
    });
    lineMaterials.push(material);
    return material;
  };
  const brightLine = lineMaterial(.82);
  const midLine = lineMaterial(.32);
  const dimLine = lineMaterial(.11);

  const graphite = new THREE.MeshPhysicalMaterial({
    color: 0x111113,
    metalness: .82,
    roughness: .24,
    clearcoat: .7,
    clearcoatRoughness: .18,
    transparent: true,
    opacity: .96,
  });
  const paleMetal = new THREE.MeshPhysicalMaterial({
    color: 0xe8e8e3,
    metalness: .7,
    roughness: .22,
    clearcoat: .62,
    clearcoatRoughness: .2,
    transparent: true,
    opacity: .98,
  });
  const formMaterial = graphite.clone();

  function segment(target, ax, ay, az, bx, by, bz) {
    target.push(ax, ay, az, bx, by, bz);
  }
  function lineSegments(target, material) {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.Float32BufferAttribute(target, 3));
    return new THREE.LineSegments(geometry, material);
  }
  function boxLines(target, x, y, z, width, height, depth) {
    const x0 = x - width / 2;
    const x1 = x + width / 2;
    const y0 = y - height / 2;
    const y1 = y + height / 2;
    const z0 = z - depth / 2;
    const z1 = z + depth / 2;
    [
      [x0,y0,z0,x1,y0,z0],[x1,y0,z0,x1,y1,z0],[x1,y1,z0,x0,y1,z0],[x0,y1,z0,x0,y0,z0],
      [x0,y0,z1,x1,y0,z1],[x1,y0,z1,x1,y1,z1],[x1,y1,z1,x0,y1,z1],[x0,y1,z1,x0,y0,z1],
      [x0,y0,z0,x0,y0,z1],[x1,y0,z0,x1,y0,z1],[x1,y1,z0,x1,y1,z1],[x0,y1,z0,x0,y1,z1],
    ].forEach(values => segment(target, ...values));
  }
  function gridPlane(target, centerY, centerZ, width, depth, spacing, majorEvery = 4) {
    const halfWidth = width / 2;
    const halfDepth = depth / 2;
    const xCount = Math.floor(width / spacing);
    const zCount = Math.floor(depth / spacing);
    for (let index = 0; index <= xCount; index += 1) {
      const x = -halfWidth + index * width / xCount;
      segment(target, x, centerY, centerZ - halfDepth, x, centerY, centerZ + halfDepth);
    }
    for (let index = 0; index <= zCount; index += 1) {
      const z = centerZ - halfDepth + index * depth / zCount;
      segment(target, -halfWidth, centerY, z, halfWidth, centerY, z);
    }
    for (let index = 0; index <= xCount; index += majorEvery) {
      const x = -halfWidth + index * width / xCount;
      segment(target, x, centerY + .015, centerZ - halfDepth, x, centerY + .015, centerZ + halfDepth);
    }
  }
  function createGlow(size, opacity) {
    const textureCanvas = document.createElement("canvas");
    textureCanvas.width = textureCanvas.height = 128;
    const context = textureCanvas.getContext("2d");
    const gradient = context.createRadialGradient(64, 64, 0, 64, 64, 64);
    gradient.addColorStop(0, "rgba(255,255,255,.95)");
    gradient.addColorStop(.18, "rgba(255,255,255,.28)");
    gradient.addColorStop(.52, "rgba(255,255,255,.07)");
    gradient.addColorStop(1, "rgba(255,255,255,0)");
    context.fillStyle = gradient;
    context.fillRect(0, 0, 128, 128);
    const texture = new THREE.CanvasTexture(textureCanvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    const material = new THREE.SpriteMaterial({
      map: texture,
      color: 0xffffff,
      transparent: true,
      opacity,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      fog: false,
    });
    const sprite = new THREE.Sprite(material);
    sprite.scale.set(size, size, 1);
    return sprite;
  }

  // SCENE 01 — CRYSTAL ENTRY
  // A single faceted passage replaces the old stack of rectangular portals.
  // It keeps a generous, uninterrupted center axis for the logo and uses one
  // authored shell rather than a loose field of decorative shards.
  const crystalEntry = new THREE.Group();
  scene.add(crystalEntry);
  const crystalDarkMaterial = new THREE.MeshPhysicalMaterial({
    color: 0x050506,
    metalness: .38,
    roughness: .5,
    clearcoat: .45,
    clearcoatRoughness: .28,
    envMapIntensity: .2,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: .96,
  });
  const crystalPaleMaterial = new THREE.MeshBasicMaterial({
    color: 0xf4f4ef,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: .22,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    toneMapped: false,
  });
  const crystalEdgeMaterial = lineMaterial(.42);
  const crystalFineMaterial = lineMaterial(.1);
  const crystalDarkPoints = [];
  const crystalPalePoints = [];
  const crystalEdgePoints = [];
  const crystalFinePoints = [];
  const entrySides = 8;
  const entryDepths = [7, -2, -12, -23, -35, -49, -65];
  const entryRings = entryDepths.map((z, ring) => {
    const points = [];
    for (let side = 0; side < entrySides; side += 1) {
      const angle = side / entrySides * Math.PI * 2 + Math.PI / 8;
      const variation = Math.sin(ring * 1.73 + side * 2.19) * .42;
      const radiusX = (mobile ? 8.15 : 12.6) + variation;
      const radiusY = (mobile ? 7.25 : 8.35) + variation * .46;
      points.push(new THREE.Vector3(
        Math.cos(angle) * radiusX,
        Math.sin(angle) * radiusY,
        z,
      ));
    }
    return points;
  });
  const pushTriangle = (target, a, b, c) => target.push(
    a.x, a.y, a.z,
    b.x, b.y, b.z,
    c.x, c.y, c.z,
  );
  entryRings.forEach((ring, ringIndex) => {
    ring.forEach((point, side) => {
      const next = ring[(side + 1) % entrySides];
      segment(crystalEdgePoints, point.x, point.y, point.z, next.x, next.y, next.z);
      if (ringIndex < entryRings.length - 1) {
        const nextRing = entryRings[ringIndex + 1];
        const forward = nextRing[side];
        const diagonal = nextRing[(side + 1) % entrySides];
        segment(
          side % 2 ? crystalFinePoints : crystalEdgePoints,
          point.x, point.y, point.z,
          forward.x, forward.y, forward.z,
        );
        segment(
          crystalFinePoints,
          next.x, next.y, next.z,
          forward.x, forward.y, forward.z,
        );
        const accent = (ringIndex * 7 + side * 5) % 13 === 0;
        const target = accent ? crystalPalePoints : crystalDarkPoints;
        if ((ringIndex + side) % 2) {
          pushTriangle(target, point, next, forward);
          pushTriangle(crystalDarkPoints, next, diagonal, forward);
        } else {
          pushTriangle(crystalDarkPoints, point, next, diagonal);
          pushTriangle(target, point, diagonal, forward);
        }
      }
    });
  });
  const crystalDarkGeometry = new THREE.BufferGeometry();
  crystalDarkGeometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(crystalDarkPoints, 3),
  );
  crystalDarkGeometry.computeVertexNormals();
  const crystalPaleGeometry = new THREE.BufferGeometry();
  crystalPaleGeometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(crystalPalePoints, 3),
  );
  crystalPaleGeometry.computeVertexNormals();
  crystalEntry.add(
    new THREE.Mesh(crystalDarkGeometry, crystalDarkMaterial),
    new THREE.Mesh(crystalPaleGeometry, crystalPaleMaterial),
    lineSegments(crystalEdgePoints, crystalEdgeMaterial),
    lineSegments(crystalFinePoints, crystalFineMaterial),
  );

  // The type belongs to the world, so the crystal shell can naturally occlude
  // it. The accessible HTML heading remains in the document but is visually
  // suppressed once WebGL is active.
  const typeCanvas = document.createElement("canvas");
  typeCanvas.width = mobile ? 1024 : 2048;
  typeCanvas.height = mobile ? 1024 : 512;
  const typeTexture = new THREE.CanvasTexture(typeCanvas);
  typeTexture.colorSpace = THREE.SRGBColorSpace;
  function drawEntryType() {
    const context = typeCanvas.getContext("2d");
    context.clearRect(0, 0, typeCanvas.width, typeCanvas.height);
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillStyle = "rgba(255,255,255,.96)";
    if (mobile) {
      context.font = "800 330px Syne, Arial Black, sans-serif";
      context.fillText("RIZE", typeCanvas.width / 2, 300);
      context.fillText("UP", typeCanvas.width / 2, 750);
    } else {
      context.font = "800 360px Syne, Arial Black, sans-serif";
      context.fillText("RIZE UP", typeCanvas.width / 2, typeCanvas.height / 2 + 12);
    }
    typeTexture.needsUpdate = true;
  }
  drawEntryType();
  document.fonts?.ready.then(drawEntryType);
  const entryTypeMaterial = new THREE.MeshBasicMaterial({
    map: typeTexture,
    transparent: true,
    opacity: .42,
    depthWrite: false,
    toneMapped: false,
  });
  const entryType = new THREE.Mesh(
    new THREE.PlaneGeometry(mobile ? 15 : 54, mobile ? 15 : 13.5),
    entryTypeMaterial,
  );
  entryType.position.set(0, mobile ? 0 : 6.2, mobile ? -22.5 : -30);
  crystalEntry.add(entryType);

  // One continuous measured world. The route descends 58 world units from the
  // home-base mark to the page threshold instead of orbiting a fixed chamber.
  const architecture = new THREE.Group();
  scene.add(architecture);

  const gridMajor = [];
  const gridMinor = [];
  [
    [0, -22, 50, 60],
    [-13, -55, 58, 54],
    [-28, -92, 66, 58],
    [-43, -130, 72, 64],
    [-54, -174, 78, 76],
  ].forEach(([y, z, width, depth], index) => {
    gridPlane(index % 2 ? gridMinor : gridMajor, y, z, width, depth, 4);
  });

  // Parallel vertical rails make the change in elevation legible from every
  // camera angle; all values use a four-unit measurement system.
  for (let side = -1; side <= 1; side += 2) {
    [-24, -16, -8].forEach((offset, index) => {
      const x = side * (Math.abs(offset) + 8);
      segment(gridMinor, x, 8, -8 - index * 4, x, -58, -205 - index * 5);
    });
  }
  architecture.add(lineSegments(gridMajor, midLine), lineSegments(gridMinor, dimLine));

  const frameCenters = [
    [0, 1, -26, 28, 18],
    [0, -3, -39, 30, 19.5],
    [0, -8, -52, 33, 21],
    [0, -14, -65, 35, 23],
    [0, -20, -78, 38, 25],
    [0, -27, -93, 41, 27],
    [0, -34, -109, 44, 29],
    [0, -40, -126, 48, 31],
    [0, -46, -145, 51, 33],
    [0, -51, -165, 55, 34],
    [0, -54, -185, 59, 35],
    [0, -54, -207, 64, 36],
    [0, -54, -206, 40, 24],
    [0, -54, -205, 30, 18],
  ];
  const frameRailMaterial = graphite.clone();
  const frameRails = new THREE.InstancedMesh(
    new THREE.BoxGeometry(1, 1, 1),
    frameRailMaterial,
    frameCenters.length * 4,
  );
  frameRails.instanceMatrix.setUsage(THREE.StaticDrawUsage);
  const matrix = new THREE.Matrix4();
  let frameInstance = 0;
  frameCenters.forEach(([x, y, z, width, height]) => {
    const rail = index => {
      const horizontal = index < 2;
      const px = horizontal ? x : x + (index === 2 ? -width / 2 : width / 2);
      const py = horizontal ? y + (index === 0 ? -height / 2 : height / 2) : y;
      const sx = horizontal ? width : .28;
      const sy = horizontal ? .28 : height;
      matrix.compose(
        new THREE.Vector3(px, py, z),
        new THREE.Quaternion(),
        new THREE.Vector3(sx, sy, .32),
      );
      frameRails.setMatrixAt(frameInstance++, matrix);
    };
    [0, 1, 2, 3].forEach(rail);
  });
  architecture.add(frameRails);

  const frameOutlines = [];
  frameCenters.forEach(([x, y, z, width, height], index) => {
    boxLines(frameOutlines, x, y, z, width + .6, height + .6, .52);
    const tick = index % 2 ? 1.2 : .8;
    segment(frameOutlines, -width / 2 - tick, y, z, -width / 2, y, z);
    segment(frameOutlines, width / 2, y, z, width / 2 + tick, y, z);
  });
  const frameOutlineMaterial = lineMaterial(.82);
  const frameOutlineLines = lineSegments(frameOutlines, frameOutlineMaterial);
  architecture.add(frameOutlineLines);

  // A restrained field of orthogonal solids: no shards, orbs, columns, or
  // decorative filler. The forms shift from dispersed to calibrated rows.
  const formLayout = [
    [-17, 6, -39, 8, .55, 3.6], [15, -2, -44, .5, 9, 3], [-20, -8, -60, 10, .42, 4],
    [18, -14, -66, .48, 11, 2.8], [-15, -20, -79, 6, .72, 7], [21, -25, -86, 8, .46, 3],
    [-24, -30, -98, .5, 12, 2.6], [13, -34, -105, 11, .5, 4], [-18, -40, -119, 5, .65, 2],
    [23, -43, -127, 8, .42, 6], [-12, -48, -138, .5, 9, 3], [18, -51, -146, 9, .44, 2],
    [-23, -54, -156, 6, .55, 5], [11, -55, -164, .48, 8, 2], [-15, -55, -173, 8, .45, 4],
    [22, -55, -182, .5, 10, 2],
  ];
  const forms = new THREE.InstancedMesh(
    new THREE.BoxGeometry(1, 1, 1),
    formMaterial,
    formLayout.length,
  );
  forms.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  architecture.add(forms);

  const formEdges = [];
  formLayout.forEach(([x, y, z, width, height, depth]) =>
    boxLines(formEdges, x, y, z, width, height, depth));
  const formEdgeMaterial = lineMaterial(.45);
  architecture.add(lineSegments(formEdges, formEdgeMaterial));

  // The RiZe mark is the home-base focal object, disappears during the descent,
  // and returns only at the inversion and the final threshold.
  const logoPoints = [
    [530.335,84.784],[2469.67,84.784],[1917.18,637.153],[2195.3,915.216],
    [1804.43,915.216],[1563.89,674.729],[1116.64,674.729],[883.624,441.757],
    [1721.75,441.757],[1842.01,321.514],[763.355,321.514],
  ];
  const logoShape = new THREE.Shape();
  logoPoints.forEach(([x, y], index) => {
    const px = (x - 1500) * .008;
    const py = (500 - y) * .008;
    if (index) logoShape.lineTo(px, py);
    else logoShape.moveTo(px, py);
  });
  logoShape.closePath();
  const logoGeometry = new THREE.ExtrudeGeometry(logoShape, {
    depth: 1.65,
    steps: 1,
    bevelEnabled: true,
    bevelThickness: .24,
    bevelSize: .16,
    bevelSegments: 4,
  });
  logoGeometry.center();
  const logo = new THREE.Group();
  const logoMesh = new THREE.Mesh(logoGeometry, paleMetal);
  const logoEdgesMaterial = lineMaterial(.96);
  const logoEdges = new THREE.LineSegments(new THREE.EdgesGeometry(logoGeometry, 18), logoEdgesMaterial);
  logo.add(logoMesh, logoEdges);
  scene.add(logo);
  const logoGlow = createGlow(34, .17);
  scene.add(logoGlow);

  const verticalSignal = createGlow(68, .05);
  verticalSignal.scale.set(24, 88, 1);
  scene.add(verticalSignal);

  // One seeded atmospheric layer. It establishes depth without unstructured
  // decorative geometry or per-particle draw calls.
  let seed = 0x52495a45;
  const random = () => {
    seed = Math.imul(seed ^ seed >>> 15, 1 | seed);
    seed ^= seed + Math.imul(seed ^ seed >>> 7, 61 | seed);
    return ((seed ^ seed >>> 14) >>> 0) / 4294967296;
  };
  const dustPositions = [];
  for (let index = 0; index < (mobile ? 280 : 540); index += 1) {
    dustPositions.push(
      (random() - .5) * 78,
      12 - random() * 82,
      20 - random() * 245,
    );
  }
  const dustGeometry = new THREE.BufferGeometry();
  dustGeometry.setAttribute("position", new THREE.Float32BufferAttribute(dustPositions, 3));
  const dustMaterial = new THREE.PointsMaterial({
    color: 0xffffff,
    size: mobile ? .052 : .038,
    transparent: true,
    opacity: .25,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const dust = new THREE.Points(dustGeometry, dustMaterial);
  scene.add(dust);

  const routePositions = [
    new THREE.Vector3(0, 0, 22),
    new THREE.Vector3(0, 0, -2),
    new THREE.Vector3(-2, -6, -36),
    new THREE.Vector3(3, -17, -70),
    new THREE.Vector3(-3, -31, -108),
    new THREE.Vector3(2, -44, -145),
    new THREE.Vector3(0, -53, -180),
  ];
  const routeTargets = [
    new THREE.Vector3(0, 0, -18),
    new THREE.Vector3(0, -2, -30),
    new THREE.Vector3(0, -12, -60),
    new THREE.Vector3(0, -25, -94),
    new THREE.Vector3(0, -39, -132),
    new THREE.Vector3(0, -51, -170),
    new THREE.Vector3(0, -54, -215),
  ];
  const route = new THREE.CatmullRomCurve3(routePositions, false, "catmullrom", .42);
  const targetRoute = new THREE.CatmullRomCurve3(routeTargets, false, "catmullrom", .42);
  const cameraTarget = new THREE.Vector3();

  const ready = mobile ? Promise.resolve() : new Promise(resolve => {
    const manager = new THREE.LoadingManager();
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    manager.onLoad = finish;
    manager.onError = finish;
    new RGBELoader(manager)
      .setDataType(THREE.HalfFloatType)
      .load("assets/hdri/monochrome_studio_01_1k.hdr", texture => {
        texture.mapping = THREE.EquirectangularReflectionMapping;
        const generator = new THREE.PMREMGenerator(renderer);
        generator.compileEquirectangularShader();
        const environment = generator.fromEquirectangular(texture).texture;
        scene.environment = environment;
        texture.dispose();
        generator.dispose();
        finish();
      }, undefined, finish);
    setTimeout(finish, 3500);
  });

  let targetProgress = 0;
  let progress = 0;
  let pointerX = 0;
  let pointerY = 0;
  let visible = true;
  let raf = 0;
  let lastTime = performance.now();
  let qualityFrames = 0;
  let qualityTime = 0;
  let needsRender = true;
  const quaternion = new THREE.Quaternion();
  const scale = new THREE.Vector3();
  const position = new THREE.Vector3();
  const forward = new THREE.Vector3();

  function updateFormInstances(value) {
    const calibration = smooth((value - .35) / .25);
    formLayout.forEach(([x, y, z, width, height, depth], index) => {
      const side = index % 2 ? 1 : -1;
      const row = Math.floor(index / 2);
      const calibratedX = side * (11 + (row % 3) * 4);
      const calibratedY = -24 - row * 4.2;
      const calibratedZ = -100 - row * 8.5;
      position.set(
        mix(x, calibratedX, calibration),
        mix(y, calibratedY, calibration),
        mix(z, calibratedZ, calibration),
      );
      quaternion.setFromEuler(new THREE.Euler(
        mix(index % 3 * .16, 0, calibration),
        mix(index % 4 * .2, index % 2 ? .08 : -.08, calibration),
        mix(index % 2 * .18, 0, calibration),
      ));
      scale.set(width, height, depth);
      matrix.compose(position, quaternion, scale);
      forms.setMatrixAt(index, matrix);
    });
    forms.instanceMatrix.needsUpdate = true;
  }

  function updateLogo(value, time, inversion) {
    const opening = 1 - smooth((value - .115) / .075);
    const inversionReturn = fadeWindow(value, .52, .59, .71, .79);
    const finalReturn = smooth((value - .82) / .11);
    const visibility = Math.max(opening, inversionReturn, finalReturn);
    logo.visible = visibility > .002;
    logoGlow.visible = logo.visible;
    if (opening >= inversionReturn && opening >= finalReturn) {
      logo.position.set(0, 0, -18);
      logo.scale.setScalar((mobile ? .72 : .96) * mix(.98, 1.08, 1 - opening));
      logo.rotation.set(0, value * .32, 0);
    } else if (inversionReturn >= finalReturn) {
      camera.getWorldDirection(forward);
      logo.position.copy(camera.position).add(forward.multiplyScalar(mobile ? 18 : 25));
      logo.quaternion.copy(camera.quaternion);
      logo.rotateY(Math.PI);
      logo.scale.setScalar(mobile ? .3 : .55);
    } else {
      const handoff = smooth((value - .95) / .05);
      logo.position.set(0, -53.5, -205);
      logo.scale.setScalar(
        (mobile ? .72 : .9) *
        mix(.82, 1.16, finalReturn) *
        mix(1, .72, handoff),
      );
      logo.rotation.set(0, mix(-.22, 0, finalReturn), 0);
    }
    const lightFace = inversion < .5;
    logoMesh.material.color.set(lightFace ? 0xe8e8e3 : 0x111111);
    logoMesh.material.emissive.set(lightFace ? 0x0c0c0c : 0x000000);
    logoMesh.material.emissiveIntensity = lightFace ? .28 : 0;
    logoMesh.material.opacity = visibility;
    logoEdges.material.opacity = visibility * mix(.95, .72, inversion);
    logo.position.x += Math.sin(time * .00018) * .025 * (1 - finalReturn);
    logoGlow.position.copy(logo.position);
    logoGlow.position.z -= 1.2;
    logoGlow.material.opacity = visibility * mix(.19, .045, inversion);
    logoGlow.scale.setScalar(
      mix(29, 19, inversion) *
      mix(1, 1.3, finalReturn) *
      mix(1, .78, smooth((value - .95) / .05)),
    );
    signalLight.position.copy(logo.position);
    signalLight.position.z += 5;
    signalLight.intensity = visibility * mix(11, 2.4, inversion);
  }

  function resize() {
    const width = container.clientWidth || innerWidth;
    const height = container.clientHeight || innerHeight;
    renderer.setPixelRatio(pixelRatio);
    renderer.setSize(width, height, false);
    camera.aspect = width / Math.max(1, height);
    camera.updateProjectionMatrix();
    needsRender = true;
  }

  function render(time) {
    raf = requestAnimationFrame(render);
    if (!visible || document.hidden) {
      lastTime = time;
      return;
    }
    const delta = Math.min(.05, (time - lastTime) / 1000);
    lastTime = time;
    if (reduced) progress = targetProgress;
    else progress += (targetProgress - progress) * (1 - Math.exp(-delta * 10.5));
    const settling = Math.abs(progress - targetProgress) > .00008;
    if (!settling && !needsRender && Math.floor(time / 120) % 2) return;
    needsRender = false;

    const routeProgress = smooth(progress);
    const cameraPosition = route.getPointAt(routeProgress);
    cameraTarget.copy(targetRoute.getPointAt(routeProgress));
    camera.position.copy(cameraPosition);
    if (!mobile) {
      camera.position.x += pointerX * .34;
      camera.position.y -= pointerY * .2;
      cameraTarget.x += pointerX * .11;
    } else {
      cameraTarget.y -= 3.5 * smooth((progress - .13) / .12);
    }
    camera.lookAt(cameraTarget);
    camera.rotateZ(Math.sin(progress * Math.PI * 4) * .006);

    const inversion = fadeWindow(progress, .62, .66, .80, .85);
    background.copy(darkBackground).lerp(paleBackground, inversion);
    signal.copy(paleSignal).lerp(darkSignal, inversion);
    scene.background.copy(background);
    scene.fog.color.copy(background);
    lineMaterials.forEach(material => material.color.copy(signal));
    dustMaterial.color.copy(signal);
    graphite.color.setRGB(
      mix(.055, .74, inversion),
      mix(.055, .74, inversion),
      mix(.062, .72, inversion),
    );
    graphite.opacity = 1 - inversion * .98;
    frameRailMaterial.color.copy(graphite.color);
    formMaterial.color.copy(graphite.color);
    paleMetal.color.setRGB(
      mix(.9, .055, inversion),
      mix(.9, .055, inversion),
      mix(.88, .055, inversion),
    );
    const descent = smooth((progress - .12) / .56);
    const entryVisibility = 1 - smooth((progress - .105) / .065);
    const measuredVisibility = smooth((progress - .145) / .07);
    crystalEntry.visible = entryVisibility > .002;
    crystalDarkMaterial.opacity = entryVisibility * .96;
    crystalPaleMaterial.opacity = entryVisibility * .22;
    crystalEdgeMaterial.opacity = entryVisibility * .42;
    crystalFineMaterial.opacity = entryVisibility * .1;
    entryTypeMaterial.opacity =
      (1 - smooth((progress - .075) / .07)) * (mobile ? .32 : .72);
    frameRails.visible = measuredVisibility > .002;
    frameRailMaterial.opacity = measuredVisibility * (1 - inversion * .98);
    frameOutlineLines.visible = measuredVisibility > .002;
    frameOutlineMaterial.opacity =
      measuredVisibility * mix(.82, .96, descent) *
      (1 - smooth((progress - .93) / .07));
    brightLine.opacity = mix(.82, .98, descent) * (1 - smooth((progress - .93) / .07));
    midLine.opacity = mix(.24, .43, descent);
    dimLine.opacity = mix(.075, .15, descent);
    formEdgeMaterial.opacity = fadeWindow(progress, .2, .28, .36, .46) * .52;
    forms.material.opacity = fadeWindow(progress, .19, .3, .54, .62) * .94;
    forms.visible = forms.material.opacity > .002;
    architecture.rotation.y = Math.sin(progress * Math.PI * 2) * .008;
    updateFormInstances(progress);
    updateLogo(progress, time, inversion);

    dust.material.opacity = mix(.18, .31, descent) * mix(1, .46, inversion);
    dust.rotation.y = time * .000004;
    verticalSignal.position.set(0, -25 - descent * 23, -87 - descent * 64);
    verticalSignal.material.opacity = fadeWindow(progress, .19, .31, .78, .9) * mix(.05, .018, inversion);

    renderer.render(scene, camera);

    if (!reduced && pixelRatio > .73) {
      qualityFrames += 1;
      qualityTime += delta;
      if (qualityFrames >= 45) {
        const average = qualityTime / qualityFrames;
        if (average > .026) {
          pixelRatio = Math.max(.72, pixelRatio * .86);
          resize();
        }
        qualityFrames = 0;
        qualityTime = 0;
      }
    }
  }

  function setProgress(value) {
    targetProgress = clamp(value);
    needsRender = true;
  }
  function pointerMove(event) {
    pointerX = (event.clientX / innerWidth - .5) * 2;
    pointerY = (event.clientY / innerHeight - .5) * 2;
    needsRender = true;
  }

  const visibilityObserver = new IntersectionObserver(([entry]) => {
    visible = entry.isIntersecting;
    needsRender = true;
  }, { rootMargin: "12% 0px" });
  visibilityObserver.observe(container);
  const pointerQuery = matchMedia("(hover:hover) and (pointer:fine)");
  if (pointerQuery.matches) addEventListener("pointermove", pointerMove, { passive: true });
  addEventListener("resize", resize, { passive: true });
  canvas.addEventListener("webglcontextlost", event => {
    event.preventDefault();
    container.classList.add("is-webgl-lost");
  }, { passive: false });
  canvas.addEventListener("webglcontextrestored", () => {
    container.classList.remove("is-webgl-lost");
    resize();
  });

  updateFormInstances(0);
  resize();
  renderer.compile(scene, camera);
  raf = requestAnimationFrame(render);

  return {
    ready,
    setProgress,
    destroy() {
      cancelAnimationFrame(raf);
      visibilityObserver.disconnect();
      removeEventListener("resize", resize);
      removeEventListener("pointermove", pointerMove);
      scene.traverse(object => {
        if (object.geometry) object.geometry.dispose();
        const materials = Array.isArray(object.material) ? object.material : [object.material];
        materials.filter(Boolean).forEach(material => {
          if (material.map) material.map.dispose();
          material.dispose();
        });
      });
      renderer.dispose();
      canvas.remove();
    },
  };
}
